/**
 * CookieConsentBanner - Minimal low-friction cookie consent
 * Stays visible at bottom until user clicks OK
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { setCookieConsent, getCookieConsent, restoreGtmConsent } from '../utils/cookieHelpers';
import { userStorage } from '../utils/UserStorage';
import { isLpJourneySearch } from '../utils/journeyMode';

const AUTO_DISMISS_MS = 20_000;

const CookieConsentBanner: React.FC = () => {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect LP routes for theming
  const isLpRoute = location.pathname.startsWith('/lp/') ||
    (location.pathname === '/free-moon-reading' || location.pathname === '/checkout') && isLpJourneySearch(location.search);

  const handleAccept = useCallback(() => {
    // Set full consent for all cookie types
    setCookieConsent({
      functional: true,
      analytics: true,
      marketing: true
    });

    // Trigger migration again with consent now given
    userStorage.migrateFromLocalStorage();

    setShowBanner(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    // Restore GTM consent state for returning visitors who already accepted.
    // The HTML default is 'denied' for ad storage, so we must re-grant on every page load.
    restoreGtmConsent();

    // LP routes grant consent in index.html, no banner needed.
    if (isLpRoute) {
      setCookieConsent({ functional: true, analytics: true, marketing: true });
      return;
    }

    // Check if user has already consented
    const consent = getCookieConsent();
    if (!consent.functional) {
      setShowBanner(true);
    }

    // Run migration from localStorage to cookies if needed
    userStorage.migrateFromLocalStorage().then((migrated) => {
      if (migrated) {
        // migrated
      }
    });
  }, []);

  // Auto-dismiss after 20 seconds, treat non-interaction as implied consent
  useEffect(() => {
    if (!showBanner) return;
    timerRef.current = setTimeout(() => {
      handleAccept();
      // auto-dismissed
    }, AUTO_DISMISS_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [showBanner, handleAccept]);

  const handleReject = () => {
    // Set minimal consent (functional only) and close banner
    setCookieConsent({
      functional: true,
      analytics: false,
      marketing: false
    });
    setShowBanner(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  if (!showBanner) return null;

  // LP theme: light background, dark text
  const containerBg = isLpRoute ? 'bg-white/95' : 'bg-forest-darkest/95';
  const borderColor = isLpRoute ? 'border-[#222222]/20' : 'border-antique-gold/60';
  const textColor = isLpRoute ? 'text-[#222222]' : 'text-manuscript-cream';
  const primaryBtnBg = isLpRoute ? 'bg-[#222222]' : 'bg-antique-gold';
  const primaryBtnText = isLpRoute ? 'text-white' : 'text-forest-deep';
  const secondaryBorder = isLpRoute ? 'border-[#222222]' : 'border-antique-gold';
  const secondaryText = isLpRoute ? 'text-[#222222]' : 'text-antique-gold';
  const secondaryHover = isLpRoute ? 'hover:bg-[#222222]/10' : 'hover:bg-antique-gold/10';
  const learnBorder = isLpRoute ? 'border-[#222222]' : 'border-manuscript-cream';
  const learnText = isLpRoute ? 'text-[#222222]' : 'text-manuscript-cream';
  const learnHover = isLpRoute ? 'hover:bg-[#222222]/10' : 'hover:bg-manuscript-cream/10';

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 px-2 pb-1 sm:px-3 sm:pb-2">
      <div className={`mx-auto max-w-5xl rounded-lg border ${borderColor} ${containerBg} shadow-lg backdrop-blur-md`}>
        <div className="flex flex-wrap items-center justify-center gap-2 px-3 py-2 sm:justify-between sm:px-4 sm:py-2.5">
          <p className={`min-w-[220px] flex-1 text-center text-xs leading-tight ${textColor} sm:text-left`}>
            We use essential and marketing cookies just like every other site on the web
          </p>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            <button
              onClick={handleAccept}
              className={`whitespace-nowrap rounded-md ${primaryBtnBg} px-3 py-1.5 text-xs font-semibold ${primaryBtnText} transition-colors hover:bg-opacity-90 sm:px-3.5 sm:text-sm`}
            >
              OK
            </button>
            <button
              onClick={handleReject}
              className={`whitespace-nowrap rounded-md border ${secondaryBorder} bg-transparent px-3 py-1.5 text-xs ${secondaryText} transition-colors ${secondaryHover} sm:px-3.5 sm:text-sm`}
            >
              Reject
            </button>
            <Link
              to="/legal/cookie-policy"
              className={`whitespace-nowrap rounded-md border ${learnBorder} bg-transparent px-3 py-1.5 text-xs ${learnText} transition-colors ${learnHover} sm:px-3.5 sm:text-sm`}
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
